import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type { ExhibitorVerification, RegistrarAccess, UpdateVerificationRequest } from "../Component/Application/ExhibitorAccessManagement/verification"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

// ✅ Dynamically attach token before each request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}
    return config
  },
  (error) => Promise.reject(error),
)

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

const VerificationService = {
  getAll: async (page = 1, pageSize = 10): Promise<PaginatedResponse<ExhibitorVerification>> => {
    const response = await api.get(`/api/exhibitorsaccess/exhibitor-verifications/?page=${page}&page_size=${pageSize}`)
    return response.data
  },

  getById: async (id: number): Promise<ExhibitorVerification> => {
    const response = await api.get(`/api/exhibitorsaccess/exhibitor-verifications/${id}/`)
    return response.data
  },

  async create(data: Omit<ExhibitorVerification, "id">): Promise<ExhibitorVerification> {
    const response = await api.post("/api/exhibitorsaccess/exhibitor-verifications/create/", data)
    return response.data
  },

  async update(id: number, data: UpdateVerificationRequest): Promise<ExhibitorVerification> {
    const response = await api.put(`/api/exhibitorsaccess/exhibitor-verifications/${id}/update/`, data)
    return response.data
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/api/exhibitorsaccess/exhibitor-verifications/${id}/delete/`)
  },

  // ✅ Custom actions
  async toggleStatus(id: number): Promise<ExhibitorVerification> {
    const response = await api.post(`/api/exhibitorsaccess/exhibitor-verifications/${id}/toggle-status/`)
    return response.data
  },

  async markAsExpired(id: number): Promise<ExhibitorVerification> {
    const response = await api.post(`/api/exhibitorsaccess/exhibitor-verifications/${id}/mark-expired/`)
    return response.data
  },

  async getStatistics(): Promise<{
    total: number
    pending: number
    verified: number
    expired: number
    failed: number
    time_expired: number
  }> {
    const response = await api.get("/api/exhibitorsaccess/exhibitor-verifications/statistics/")
    return response.data
  },

  // ✅ Registrar Access methods
  getRegistrarAccess: async (page = 1, pageSize = 10): Promise<PaginatedResponse<RegistrarAccess>> => {
    const response = await api.get(`/api/exhibitorsaccess/registrar-access/?page=${page}&page_size=${pageSize}`)
    return response.data
  },

  getRegistrarAccessById: async (id: number): Promise<RegistrarAccess> => {
    const response = await api.get(`/api/exhibitorsaccess/registrar-access/${id}/`)
    return response.data
  },

  async updateRegistrarAccess(id: number, data: { is_active: boolean }): Promise<RegistrarAccess> {
    const response = await api.put(`/api/exhibitorsaccess/registrar-access/${id}/update/`, data)
    return response.data
  },

  async toggleRegistrarAccess(id: number): Promise<RegistrarAccess> {
    const response = await api.post(`/api/exhibitorsaccess/registrar-access/${id}/toggle-active/`)
    return response.data
  },
}

export default VerificationService
